import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldWarning, ArrowLeft, Prohibit, CheckCircle, Flag, Warning, CaretDown, CaretRight,
  ShieldCheck, ShieldSlash, Broadcast, Globe, ArrowUUpLeft, ArrowSquareOut,
  Plus, Trash, Star, ArrowsClockwise, Power,
} from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [openMode, setOpenMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [live, setLive] = useState(true);
  const timerRef = useRef(null);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const [checkingDomains, setCheckingDomains] = useState(false);

  const loadDomains = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/domains");
      setDomains(data);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, cfg] = await Promise.all([
        api.get("/admin/reports"), api.get("/admin/stats"), api.get("/admin/settings"),
      ]);
      setItems(r.data);
      setStats(s.data);
      setOpenMode(cfg.data.open_redirect_mode);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Failed to load reports");
    } finally { setLoading(false); }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/redirect-logs?limit=40");
      setLogs(data);
    } catch { /* ignore transient */ }
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") { navigate("/dashboard"); return; }
    load();
    loadLogs();
    loadDomains();
  }, [user, load, loadLogs, loadDomains, navigate]);

  const addDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;
    try {
      await api.post("/admin/domains", { domain: newDomain.trim() });
      setNewDomain("");
      toast.success("Domain added");
      loadDomains();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setDefaultDomain = async (d) => {
    try { await api.post("/admin/domains/default", { domain_id: d.domain_id }); toast.success(`${d.domain} is now default`); loadDomains(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggleDomain = async (d) => {
    try { await api.post(`/admin/domains/${d.domain_id}/toggle`); loadDomains(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const deleteDomain = async (d) => {
    if (!window.confirm(`Remove ${d.domain}?`)) return;
    try { await api.delete(`/admin/domains/${d.domain_id}`); toast.success("Domain removed"); loadDomains(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const recheckDomains = async () => {
    setCheckingDomains(true);
    try { const { data } = await api.post("/admin/domains/check"); setDomains(data); toast.success("Health refreshed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setCheckingDomains(false); }
  };

  useEffect(() => {
    if (live) {
      timerRef.current = setInterval(loadLogs, 4000);
      return () => clearInterval(timerRef.current);
    }
  }, [live, loadLogs]);

  const toggleSafety = async () => {
    const next = !openMode; // openMode true == safety OFF
    setSaving(true);
    try {
      await api.post("/admin/settings", { open_redirect_mode: next });
      setOpenMode(next);
      toast.success(next ? "Open redirect ON — safety checks disabled" : "Safety checks RE-ENABLED");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const block = async (l) => {
    try {
      await api.post(`/admin/links/${l.link_id}/block`);
      toast.success(`/r/${l.code} blocked`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const clear = async (l) => {
    try {
      await api.post(`/admin/links/${l.link_id}/clear`);
      toast.success(`/r/${l.code} cleared`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="sticky top-0 z-40 bg-[#050505] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard-btn"
              className="w-9 h-9 border border-white/15 flex items-center justify-center hover:border-white transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              <ShieldWarning size={18} weight="bold" className="text-yellow-500" />
              <span className="font-head font-extrabold tracking-tighter text-lg">MODERATION</span>
            </div>
          </div>
          <span className="mono text-xs text-zinc-500 hidden sm:block">{user?.email}</span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <p className="label-xs mb-2">// moderation console</p>
        <h1 className="font-head text-3xl sm:text-4xl font-black tracking-tighter uppercase mb-8">Control room</h1>

        {/* Kill switch */}
        {openMode !== null && (
          <div className={`border p-5 mb-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${openMode ? "border-red-500/40 bg-red-500/5" : "border-green-500/40 bg-green-500/5"}`} data-testid="kill-switch">
            <div className="flex items-center gap-3">
              {openMode ? <ShieldSlash size={26} className="text-red-500 shrink-0" weight="bold" />
                        : <ShieldCheck size={26} className="text-green-500 shrink-0" weight="bold" />}
              <div>
                <p className="font-head font-bold text-sm uppercase tracking-wider" data-testid="safety-status">
                  {openMode ? "Open redirect — safety OFF" : "Protected — safety ON"}
                </p>
                <p className="text-zinc-400 text-xs mt-0.5">
                  {openMode
                    ? "Anonymous creation allowed, no URL checks, instant redirects to anything."
                    : "Login required, Safe Browsing + scheme checks on create and on redirect."}
                </p>
              </div>
            </div>
            <button onClick={toggleSafety} disabled={saving} data-testid="toggle-safety-btn"
              className={`h-11 px-6 font-head font-bold text-sm uppercase tracking-wider transition-colors disabled:opacity-50 shrink-0 ${openMode ? "bg-green-500 text-black hover:bg-green-400" : "bg-red-500 text-white hover:bg-red-600"}`}>
              {saving ? "..." : (openMode ? "Re-enable safety" : "Disable safety")}
            </button>
          </div>
        )}

        {/* Branded domains */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe size={16} className="text-white" weight="bold" />
            <p className="label-xs">// branded domains</p>
          </div>
          <button onClick={recheckDomains} disabled={checkingDomains} data-testid="recheck-domains-btn"
            className="flex items-center gap-1.5 mono text-xs uppercase tracking-wider border border-white/15 px-3 h-8 hover:border-white transition-colors disabled:opacity-50">
            <ArrowsClockwise size={13} className={checkingDomains ? "animate-spin" : ""} /> Check health
          </button>
        </div>
        <div className="border border-white/10 bg-[#0a0a0a] mb-4" data-testid="domains-list">
          {domains.length === 0 && <p className="p-4 text-zinc-600 text-sm mono">no domains configured</p>}
          {domains.map((d) => (
            <div key={d.domain_id} className="flex items-center gap-3 px-4 py-3 border-b border-white/10 last:border-b-0" data-testid={`domain-row-${d.domain}`}>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${d.health === "up" ? "bg-green-500" : d.health === "down" ? "bg-red-500" : "bg-zinc-600"}`} title={d.health} />
              <span className="mono text-sm text-white flex-1 truncate">{d.domain}</span>
              {d.is_default && <span className="mono text-[10px] uppercase tracking-widest border border-yellow-500/40 text-yellow-400 px-2 py-0.5 flex items-center gap-1"><Star size={10} weight="fill" />default</span>}
              {!d.enabled && <span className="mono text-[10px] uppercase tracking-widest border border-white/20 text-zinc-500 px-2 py-0.5">disabled</span>}
              <span className="mono text-[10px] uppercase tracking-widest text-zinc-500 w-14 text-right">{d.health}</span>
              <div className="flex items-center gap-1">
                {!d.is_default && (
                  <button onClick={() => setDefaultDomain(d)} data-testid={`set-default-${d.domain}`} title="Set default"
                    className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-yellow-500 hover:text-yellow-400 transition-colors"><Star size={14} /></button>
                )}
                <button onClick={() => toggleDomain(d)} data-testid={`toggle-domain-${d.domain}`} title={d.enabled ? "Disable" : "Enable"}
                  className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-white transition-colors"><Power size={14} /></button>
                <button onClick={() => deleteDomain(d)} data-testid={`delete-domain-${d.domain}`} title="Remove"
                  className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-red-500 hover:text-red-500 transition-colors"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={addDomain} className="flex gap-2 mb-10">
          <input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="go.yourbrand.com"
            data-testid="new-domain-input"
            className="flex-1 h-11 bg-transparent border border-white/10 px-3 text-sm mono placeholder:text-zinc-600 focus:outline-none focus:border-white transition-colors" />
          <button type="submit" data-testid="add-domain-btn"
            className="h-11 px-5 bg-white text-black font-head font-bold text-sm uppercase tracking-wider flex items-center gap-2 hover:bg-zinc-300 transition-colors">
            <Plus size={15} weight="bold" /> Add
          </button>
        </form>

        <p className="label-xs mb-2">// report inbox</p>
        <h2 className="font-head text-2xl font-black tracking-tighter uppercase mb-6">Reported links</h2>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10">
            <Stat label="Flagged" value={stats.flagged_links} testid="stat-flagged" />
            <Stat label="Blocked" value={stats.blocked_links} testid="stat-blocked" />
            <Stat label="Reports" value={stats.total_reports} testid="stat-reports" />
            <Stat label="Total links" value={stats.total_links} testid="stat-total" />
          </div>
        )}

        {loading && <p className="mono text-sm text-zinc-500">loading<span className="cursor-blink">_</span></p>}

        {!loading && items.length === 0 && (
          <div className="border border-white/10 p-12 text-center" data-testid="empty-reports">
            <CheckCircle size={28} className="text-green-500 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Inbox zero — no reported links right now.</p>
          </div>
        )}

        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.link_id} className="border border-white/10 bg-[#0a0a0a]" data-testid={`report-item-${l.code}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 p-4">
                <button onClick={() => setExpanded(expanded === l.link_id ? null : l.link_id)}
                  data-testid={`toggle-reports-${l.code}`}
                  className="text-zinc-500 hover:text-white transition-colors shrink-0">
                  {expanded === l.link_id ? <CaretDown size={16} /> : <CaretRight size={16} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <a href={`/r/${l.code}`} target="_blank" rel="noreferrer" className="mono text-white hover:underline">/r/{l.code}</a>
                    <span className="mono text-[10px] uppercase tracking-widest border px-2 py-0.5 text-red-400 border-red-500/40 flex items-center gap-1">
                      <Flag size={11} />{l.reports_count} report{l.reports_count > 1 ? "s" : ""}
                    </span>
                    {l.status === "blocked" && (
                      <span className="mono text-[10px] uppercase tracking-widest border px-2 py-0.5 text-red-400 border-red-500/40">blocked</span>
                    )}
                  </div>
                  <div className="mono text-xs text-zinc-500 truncate">{l.destination_url}</div>
                  <div className="mono text-[11px] text-zinc-600 mt-1">owner: {l.owner_email}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => block(l)} disabled={l.status === "blocked"} data-testid={`block-btn-${l.code}`}
                    className="h-9 px-4 mono text-xs uppercase tracking-wider border border-red-500/40 text-red-400 flex items-center gap-2 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                    <Prohibit size={14} /> Block
                  </button>
                  <button onClick={() => clear(l)} data-testid={`clear-btn-${l.code}`}
                    className="h-9 px-4 mono text-xs uppercase tracking-wider border border-green-500/40 text-green-400 flex items-center gap-2 hover:bg-green-500/10 transition-colors">
                    <CheckCircle size={14} /> Clear
                  </button>
                </div>
              </div>
              {expanded === l.link_id && (
                <div className="border-t border-white/10 p-4 space-y-2" data-testid={`reports-detail-${l.code}`}>
                  {l.reports.map((r, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <Warning size={14} className="text-yellow-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-zinc-300">{r.reason}</p>
                        <p className="mono text-[11px] text-zinc-600">{new Date(r.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Live redirect feed */}
        <div className="flex items-center justify-between mt-14 mb-4">
          <div className="flex items-center gap-2">
            <Broadcast size={16} className={live ? "text-green-500" : "text-zinc-500"} weight="bold" />
            <p className="label-xs">// live redirects</p>
            {live && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
          </div>
          <button onClick={() => setLive((v) => !v)} data-testid="toggle-live-btn"
            className="mono text-xs uppercase tracking-wider border border-white/15 px-3 h-8 hover:border-white transition-colors">
            {live ? "Pause" : "Resume"}
          </button>
        </div>
        <div className="border border-white/10 overflow-x-auto" data-testid="redirect-feed">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="label-xs text-left">
                <th className="p-3 border-r border-white/10 font-normal">When</th>
                <th className="p-3 border-r border-white/10 font-normal">Code</th>
                <th className="p-3 border-r border-white/10 font-normal">Destination</th>
                <th className="p-3 border-r border-white/10 font-normal">Referrer</th>
                <th className="p-3 font-normal">Country</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-zinc-600 text-sm">no redirects yet</td></tr>
              )}
              {logs.map((e, i) => (
                <tr key={i} className="border-t border-white/10 hover:bg-white/[0.02] transition-colors" data-testid="redirect-row">
                  <td className="p-3 border-r border-white/10 mono text-xs text-zinc-400 whitespace-nowrap">{timeAgo(e.ts)}</td>
                  <td className="p-3 border-r border-white/10 mono text-xs text-white">/r/{e.code}</td>
                  <td className="p-3 border-r border-white/10 max-w-[300px]">
                    <a href={e.destination || "#"} target="_blank" rel="noreferrer" className="mono text-xs text-zinc-300 truncate hover:underline flex items-center gap-1">
                      <span className="truncate">{e.destination || "—"}</span>
                      {e.destination && <ArrowSquareOut size={11} className="shrink-0 text-zinc-600" />}
                    </a>
                  </td>
                  <td className="p-3 border-r border-white/10 mono text-xs text-zinc-400 flex items-center gap-1"><ArrowUUpLeft size={12} className="text-zinc-600" />{e.referrer}</td>
                  <td className="p-3 mono text-xs text-zinc-400"><span className="flex items-center gap-1"><Globe size={12} className="text-zinc-600" />{e.country}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return "—"; }
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-[#0a0a0a] px-5 py-4" data-testid={testid}>
      <div className="label-xs mb-1">{label}</div>
      <div className="font-head text-2xl font-black tabular-nums">{value}</div>
    </div>
  );
}
